import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { X, LayoutGrid, ChevronLeft, ChevronRight, ArrowLeft, Bookmark, BookmarkCheck, Check, Share2 } from 'lucide-react';
import { studyAPI, cardsAPI } from '../../services/api';
import { markRememberDone } from './rememberProgress';
import CommentSection from '../CommentSection/CommentSection';
import { QuestionCard, AnswerBlock, isMCQCard } from './QuestionCard';
import { restoreStudyQueue } from './studySessionRecovery';
import useReviewOutbox from './useReviewOutbox';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import './CardBrowser.css';
import './StudySession.css';
import './StudySessionCB.css';

// ── ShareButton ──────────────────────────────────────────────────────────────
function ShareButton({ cardId }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `https://civilfox.app/card/${cardId}`;
    setError('');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'CivilFox Card', url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (shareError) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (copyError) {
        console.error('Could not share card:', shareError, copyError);
        setError('Could not share');
      }
    }
  };

  return (
    <>
      <button className="cb-icon-action" onClick={handleShare} title={error || (copied ? 'Link copied' : 'Share this card')} aria-label="Share card">
        {copied ? <Check size={18} /> : <Share2 size={18} />}
      </button>
      {error && <span className="cb-action-error" role="alert">{error}</span>}
    </>
  );
}

// ── BookmarkButton ───────────────────────────────────────────────────────────
function BookmarkButton({ cardId, isBookmarked, onChange }) {
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  const toggle = async (e) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await cardsAPI.bookmark(cardId);
      onChange(Boolean(data.isBookmarked));
    } catch (err) {
      console.error('Bookmark error:', err);
      setError('Could not save bookmark');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        className={`cb-icon-action ${isBookmarked ? 'is-bookmarked' : ''}`}
        onClick={toggle}
        disabled={loading}
        title={error || (isBookmarked ? 'Remove bookmark' : 'Bookmark this card')}
        aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark this card'}
        aria-pressed={isBookmarked}
      >
        {isBookmarked ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
      </button>
      {error && <span className="cb-action-error" role="alert">{error}</span>}
    </>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
// Routes:  /prelims/:subject            → Remember (due cards) or ?mode=practice
//          /mains/:subject              → Practice only
//          /prelims/day/:date           → Practice a whole question-day, all subjects
export default function StudySession({ examType }) {
  const { subject, date: dateParam } = useParams();
  const navigate       = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const decodedSubject = decodeURIComponent(subject || '');
  const backPath       = examType === 'Prelims' ? '/prelims' : '/mains';
  const isPractice     = examType === 'Mains' || !decodedSubject || searchParams.get('mode') === 'practice';
  const studyMode      = isPractice ? 'practice' : 'remember';
  const selectedDate   = dateParam || searchParams.get('date');
  const todayDate      = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  const sessionScope   = selectedDate || (isPractice ? 'due' : todayDate);

  const SESSION_KEY = useMemo(
    () => `upsc_session_${examType}_${decodedSubject}_${studyMode}_${sessionScope}`,
    [examType, decodedSubject, studyMode, sessionScope]
  );
  const CARDS_KEY = useMemo(
    () => `upsc_cards_${examType}_${decodedSubject}_${studyMode}_${sessionScope}`,
    [examType, decodedSubject, studyMode, sessionScope]
  );

  const [allCards,      setAllCards]      = useState([]);
  const [queue,         setQueue]         = useState([]);
  const [queueIndex,    setQueueIndex]    = useState(0);   // frontier
  const [viewIndex,     setViewIndex]     = useState(0);   // what is on screen
  const [flipped,       setFlipped]       = useState(false); // frontier card answered / revealed
  const [selectedOpt,   setSelectedOpt]   = useState(null);
  const [answers,       setAnswers]       = useState({});  // { [index]: { sel, res: 'correct'|'wrong'|'seen' } }
  const [marked,        setMarked]        = useState({});  // mark-for-review flags
  const [paletteOpen,   setPaletteOpen]   = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [loadError,     setLoadError]     = useState('');
  const [serverReconciled, setServerReconciled] = useState(false);
  const [sessionDone,   setSessionDone]   = useState(false);
  const [stats,         setStats]         = useState({ reviewed: 0, correct: 0, streak: 0 });
  const [nextSubjects,  setNextSubjects]  = useState([]);
  const [capReached,    setCapReached]    = useState(false);

  const startTime     = useRef(Date.now());
  const queueIndexRef = useRef(0);
  const lastRated     = useRef(-1);
  const interactedDuringLoad = useRef(false);
  const contentRef    = useRef(null);

  useEffect(() => { queueIndexRef.current = queueIndex; }, [queueIndex]);

  const record = useCallback((i, sel, res) => setAnswers(a => ({ ...a, [i]: { sel, res } })), []);

  // A confirmed rating brings back the server's real state: streak, and fresh interval
  // labels for a card that was put back in the queue (Again / Hard)
  const onSynced = useCallback((item, data) => {
    if (data.streak != null) setStats(s => ({ ...s, streak: data.streak }));
    if (!data.progress) return;
    setQueue(q => q.map((it, i) => (
      i > queueIndexRef.current && String(it.card._id) === String(item.cardId)
        ? { ...it, progress: data.progress, preview: data.progress.preview }
        : it
    )));
  }, []);
  const {
    send: sendReview,
    retry: retryReviews,
    pending,
    stuck,
    failed,
    hasPendingCard,
  } = useReviewOutbox({ onSynced });

  const handleBookmarkChanged = useCallback((cardId, isBookmarked) => {
    interactedDuringLoad.current = true;
    setQueue(q => q.map(item => (
      String(item.card._id) === String(cardId)
        ? { ...item, progress: { ...item.progress, isBookmarked } }
        : item
    )));
  }, []);

  const completeRememberSubject = useCallback(async () => {
    if (!isPractice && decodedSubject) {
      try {
        markRememberDone(examType, decodedSubject);
        await studyAPI.markRememberDone({ examType, subject: decodedSubject });
      } catch (error) {
        console.error('Could not sync Remember progress:', error);
      }
    }
  }, [isPractice, decodedSubject, examType]);

  const finish = useCallback(() => {
    setSessionDone(true);
    if (isPractice) {
      try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
      try { sessionStorage.removeItem(CARDS_KEY); } catch (_) {}
    }
  }, [isPractice, SESSION_KEY, CARDS_KEY]);

  // ── Load cards ─────────────────────────────────────────────────────────────
  const loadCards = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setServerReconciled(false);
    setAllCards([]); setQueue([]); setQueueIndex(0); setViewIndex(0);
    setSessionDone(false); setCapReached(false);
    setStats({ reviewed: 0, correct: 0, streak: 0 });
    setNextSubjects([]); setFlipped(false); setSelectedOpt(null);
    setAnswers({}); setMarked({});
    lastRated.current = -1;

    interactedDuringLoad.current = false;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(SESSION_KEY)); } catch (_) {}

    const apply = ({ list, idx, saved, stats: restoredStats }) => {
      const restoredAnswers = isPractice
        ? saved
        : Object.fromEntries(Object.entries(saved).filter(([position]) => Number(position) < idx));
      queueIndexRef.current = idx;
      setAllCards(list); setQueue(list);
      setQueueIndex(idx); setViewIndex(idx); setAnswers(restoredAnswers);
      setStats(restoredStats || { reviewed: 0, correct: 0, streak: 0 });
      setFlipped(false); setSelectedOpt(null);
      setSessionDone(idx >= list.length && list.length > 0);
    };

    // Instant restore (prevents a blank page when a mobile tab is remounted)
    let cachedQueue = null;
    try {
      const cachedRaw = sessionStorage.getItem(CARDS_KEY);
      const cached    = cachedRaw ? JSON.parse(cachedRaw) : null;
      if (cached?.queue?.length > 0) {
        cachedQueue = cached.queue;
        const cachedIndex = Number.isInteger(cached.queueIndex)
          ? cached.queueIndex
          : (saved?.queueIndex || 0);
        const cachedIds = Array.isArray(cached.cardIds)
          ? cached.cardIds
          : cachedQueue.map(item => item.card._id);
        const cachedCurrentCardId = cached.currentCardId ?? cachedIds[cachedIndex] ?? null;
        saved = {
          ...saved,
          queueIndex: cachedIndex,
          currentCardId: cachedCurrentCardId,
          currentOccurrence: cached.currentOccurrence ?? (
            cachedCurrentCardId
              ? cachedIds.slice(0, cachedIndex).filter(id => String(id) === String(cachedCurrentCardId)).length
              : 0
          ),
          cardIds: cachedIds,
          answers: cached.answers || saved?.answers || {},
          stats: cached.stats || saved?.stats,
        };
        const r = restoreStudyQueue(null, cachedQueue, saved);
        apply(r);
        setLoading(false);
      }
    } catch (_) {}

    try {
      const res = isPractice
        ? await studyAPI.getQuestionBank({ date: selectedDate || todayDate, subject: decodedSubject, examType })
        : await studyAPI.getDue({ subject: decodedSubject, examType });
      const cards = res.data.cards || [];
      setLoadError('');

      if (res.data.capReached && !isPractice) {
        setServerReconciled(true);
        setCapReached(true);
        if (cachedQueue) {
          if (interactedDuringLoad.current) {
            setLoadError('Daily review limit reached on another device. No additional ratings can be saved today.');
            completeRememberSubject();
            return;
          }
          const savedIndex = Number.isInteger(saved?.queueIndex) ? Math.max(0, saved.queueIndex) : 0;
          const completedCount = Math.min(
            interactedDuringLoad.current ? queueIndexRef.current : savedIndex,
            cachedQueue.length
          );
          const completed = cachedQueue.slice(0, completedCount);
          apply({ list: completed, idx: completed.length, saved: saved?.answers || {}, stats: saved?.stats });
          if (completed.length > 0) {
            try {
              sessionStorage.setItem(CARDS_KEY, JSON.stringify({
                queue: completed,
                queueIndex: completed.length,
                cardIds: completed.map(item => item.card._id),
                answers: saved?.answers || {},
                stats: saved?.stats,
                currentCardId: null,
                currentOccurrence: 0,
              }));
            } catch (_) {}
          } else {
            try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
            try { sessionStorage.removeItem(CARDS_KEY); } catch (_) {}
          }
        }
        completeRememberSubject();
        return;
      }
      if (cachedQueue && interactedDuringLoad.current) return;
      setServerReconciled(true);
      const pendingCardIds = cards
        .filter(item => hasPendingCard(item.card._id))
        .map(item => item.card._id);
      const r = restoreStudyQueue(cards, cachedQueue, saved, pendingCardIds);
      apply(r);
      try { sessionStorage.setItem(CARDS_KEY, JSON.stringify({ queue: r.list })); } catch (_) {}
      if (r.idx >= r.list.length && !isPractice) completeRememberSubject();
    } catch (err) {
      console.error(err);
      setLoadError(cachedQueue
        ? 'Could not refresh the server queue. Your saved session is still available.'
        : 'Could not load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [decodedSubject, examType, isPractice, selectedDate, todayDate, SESSION_KEY, CARDS_KEY, completeRememberSubject, hasPendingCard]);

  useEffect(() => { loadCards(); }, [loadCards]);

  // ── Persist progress ──────────────────────────────────────────────────────
  useEffect(() => {
    if (loading || allCards.length === 0 || (sessionDone && isPractice)) return;
    try {
      const currentCardId = queue[queueIndex]?.card._id || null;
      const snapshot = {
        queueIndex,
        currentCardId,
        currentOccurrence: currentCardId
          ? queue.slice(0, queueIndex).filter(item => String(item.card._id) === String(currentCardId)).length
          : 0,
        cardIds: queue.map(item => item.card._id),
        answers,
        stats,
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(snapshot));
      sessionStorage.setItem(CARDS_KEY, JSON.stringify({ ...snapshot, queue }));
    } catch (_) {}
  }, [queueIndex, answers, stats, sessionDone, loading, allCards.length, isPractice, SESSION_KEY, CARDS_KEY, queue]);

  useEffect(() => {
    if (!serverReconciled || !sessionDone || isPractice || pending > 0 || failed) return;
    try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
    try { sessionStorage.removeItem(CARDS_KEY); } catch (_) {}
  }, [serverReconciled, sessionDone, isPractice, pending, failed, SESSION_KEY, CARDS_KEY]);

  // ── When session done: fetch remaining subjects ───────────────────────────
  useEffect(() => {
    if (!sessionDone || isPractice) return;
    completeRememberSubject();
    studyAPI.getDueSubjects({ examType })
      .then(res => setNextSubjects((res.data.subjects || []).filter(s => s !== decodedSubject)))
      .catch(() => setNextSubjects([]));
  }, [sessionDone, examType, decodedSubject, isPractice, completeRememberSubject]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const isReviewing = viewIndex < queueIndex;
  const current     = queue[viewIndex];
  const mcq         = isMCQCard(current?.card);
  const rec         = answers[viewIndex];
  const done        = isReviewing || flipped;                       // answer side visible
  const sel         = isReviewing ? (rec?.sel ?? null) : selectedOpt;

  // Both shells are full-screen, so the page behind them must not scroll
  const showTest = !loading && allCards.length > 0 && !sessionDone && !!current;
  useEffect(() => {
    if (!showTest) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showTest]);

  useEffect(() => { contentRef.current?.scrollTo?.(0, 0); }, [viewIndex]);

  // Palette: questions grouped by the subject attached to each card
  const groups = useMemo(() => {
    const m = new Map();
    queue.forEach((it, i) => {
      const s = it.card?.subject || decodedSubject;
      if (!m.has(s)) m.set(s, []);
      m.get(s).push(i);
    });
    return [...m.entries()];
  }, [queue, decodedSubject]);

  const tally = useMemo(() => {
    const v = Object.values(answers);
    const c = v.filter(a => a.res === 'correct').length;
    const w = v.filter(a => a.res === 'wrong').length;
    const s = v.filter(a => a.res === 'seen').length;
    return { c, w, s, left: Math.max(queue.length - v.length, 0) };
  }, [answers, queue.length]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const goTo = useCallback((i) => {
    if (i < 0 || i > queueIndex || i === viewIndex) return;
    interactedDuringLoad.current = true;
    setViewIndex(i);
  }, [viewIndex, queueIndex]);

  // Previous / next index within the completed part of the queue.
  const findIdx = useCallback((dir) => {
    for (let i = viewIndex + dir; i >= 0 && i <= queueIndex; i += dir) {
      return i;
    }
    return -1;
  }, [viewIndex, queueIndex]);

  const step = useCallback((dir) => {
    const i = findIdx(dir);
    if (i >= 0) goTo(i);
  }, [findIdx, goTo]);

  // ── Answering ─────────────────────────────────────────────────────────────
  const handleOptionSelect = (i) => {
    if (!current || flipped || isReviewing) return;
    interactedDuringLoad.current = true;
    const ok = current.card.options[i]?.isCorrect === true;
    startTime.current = Date.now();
    setSelectedOpt(i);
    setFlipped(true);
    if (isPractice) record(queueIndex, i, ok ? 'correct' : 'wrong');
  };

  const handleReveal = useCallback(() => {
    if (!current || isReviewing || flipped) return;
    interactedDuringLoad.current = true;
    startTime.current = Date.now();
    setFlipped(true);
    if (isPractice) record(queueIndex, null, 'seen');
  }, [current, isReviewing, flipped, isPractice, queueIndex, record]);

  const changeStudyMode = mode => {
    const p = new URLSearchParams(searchParams);
    p.set('mode', mode);
    setSearchParams(p);
  };

  // requeue: a card to append to the queue (Again / Hard on a learning card), or null
  const goNext = useCallback((requeue) => {
    const newQ = requeue ? [...queue, requeue] : queue;
    const next = queueIndex + 1;
    queueIndexRef.current = next;
    setQueue(newQ);
    setQueueIndex(next);
    setViewIndex(next);
    setFlipped(false);
    setSelectedOpt(null);
    startTime.current = Date.now();
    if (next >= newQ.length) finish();
  }, [queue, queueIndex, finish]);

  const handlePracticeNext = useCallback(() => {
    if (!current || isReviewing || !flipped) return;
    interactedDuringLoad.current = true;
    const next = queueIndex + 1;
    queueIndexRef.current = next;
    setQueueIndex(next);
    setViewIndex(next);
    setFlipped(false);
    setSelectedOpt(null);
    if (next >= queue.length) finish();
  }, [current, isReviewing, flipped, queueIndex, queue.length, finish]);

  // Move first, sync in the background: the outbox keeps the rating until the server confirms it
  const handleRate = useCallback((button) => {
    if (!current || isReviewing || isPractice || capReached || lastRated.current === queueIndex) return;
    interactedDuringLoad.current = true;
    lastRated.current = queueIndex;

    const responseTime = Date.now() - startTime.current;
    const correct = mcq
      ? (selectedOpt !== null && current.card.options[selectedOpt]?.isCorrect === true)
      : ['good', 'easy'].includes(button);

    record(queueIndex, mcq ? selectedOpt : null, correct ? 'correct' : 'wrong');
    setStats(s => ({ ...s, reviewed: s.reviewed + 1, correct: correct ? s.correct + 1 : s.correct }));

    const learning = current.progress?.isLearning !== false;
    const again    = button === 'again';
    goNext(again || (button === 'hard' && learning)
      ? {
          card: current.card,
          progress: { ...current.progress, isLearning: true, learningStep: again ? 0 : (current.progress?.learningStep ?? 0) },
          preview: null,
        }
      : null);

    sendReview({ cardId: current.card._id, button, responseTime, examType, selectedOption: mcq ? selectedOpt : null });
  }, [current, isReviewing, isPractice, capReached, queueIndex, mcq, selectedOpt, record, goNext, sendReview, examType]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); step(-1); return; }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (isPractice && done && !isReviewing) handlePracticeNext(); else step(1);
        return;
      }
      if ((e.key === ' ' || e.key === 'Enter') && e.target.tagName !== 'BUTTON' && !done) {
        e.preventDefault();
        handleReveal();
        return;
      }
      if (!done || isReviewing || isPractice) return;
      if (e.key === '1') handleRate('again');
      if (e.key === '2') handleRate('hard');
      if (e.key === '3') handleRate('good');
      if (e.key === '4') handleRate('easy');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [done, isReviewing, isPractice, step, handleReveal, handleRate, handlePracticeNext]);

  const title = decodedSubject || 'All subjects';

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="study-loading">
      <div className="spinner" style={{ width: 44, height: 44 }} />
      <p>Loading {title}...</p>
    </div>
  );

  // ── No cards ───────────────────────────────────────────────────────────────
  if (loadError && allCards.length === 0) return (
    <div className="study-done slide-up">
      <div className="study-done-icon">⚠️</div>
      <h2>Could not load questions</h2>
      <p>{loadError}</p>
      <div className="done-actions">
        <button type="button" className="btn btn-primary btn-lg" onClick={loadCards}>Retry</button>
        <Link to={backPath} className="btn btn-secondary">← Pick Another Subject</Link>
      </div>
    </div>
  );

  if (allCards.length === 0) return (
    <div className="study-done slide-up">
      <div className="study-done-icon">{capReached ? '🎯' : '📭'}</div>
      <h2>{capReached && !isPractice ? `Daily limit reached for ${decodedSubject}` : `No ${examType} cards for ${decodedSubject || 'this day'}`}</h2>
      <p>{capReached
        ? `You've studied all 30 cards allowed today for this subject. Pick another subject or come back tomorrow.`
        : `New cards are added every morning at 6 AM.`}
      </p>
      <div className="done-actions">
        <Link to={backPath} className="btn btn-primary btn-lg">← Pick Another Subject</Link>
      </div>
    </div>
  );

  // ── Session done ───────────────────────────────────────────────────────────
  if (sessionDone) {
    const sum = isPractice
      ? { reviewed: Object.keys(answers).length, correct: tally.c }
      : stats;
    return (
      <div className="study-done slide-up">
        <div className="study-done-icon">{nextSubjects.length === 0 ? '🏆' : '✅'}</div>
        <h2>{isPractice ? 'Practice Complete' : nextSubjects.length === 0 ? 'All Done for Today!' : `${decodedSubject} Complete!`}</h2>

        <p style={{ color: 'var(--text-2)' }}>
          {isPractice
            ? <>You practiced <strong>{title}</strong>. No ratings were saved.</>
            : nextSubjects.length === 0
            ? <>Congrats! You've finished <strong>all {examType} subjects</strong> for today. See you tomorrow! 🎉</>
            : <>All <strong>{decodedSubject}</strong> cards done. Keep going!</>
          }
        </p>

        <div className="session-summary">
          <div className="summary-stat">
            <div className="summary-val">{sum.reviewed}</div>
            <div className="summary-lbl">{isPractice ? 'Questions attempted' : 'Reviewed'}</div>
          </div>
          <div className="summary-stat">
            <div className="summary-val" style={{ color: 'var(--green-bright)' }}>{sum.correct}</div>
            <div className="summary-lbl">Correct</div>
          </div>
          <div className="summary-stat">
            <div className="summary-val" style={{ color: 'var(--saffron)' }}>
              {sum.reviewed > 0 ? Math.round((sum.correct / sum.reviewed) * 100) : 0}%
            </div>
            <div className="summary-lbl">Accuracy</div>
          </div>
          {!isPractice && (
            <div className="summary-stat">
              <div className="summary-val" style={{ color: 'var(--saffron)' }}>🔥{stats.streak}</div>
              <div className="summary-lbl">Streak</div>
            </div>
          )}
        </div>

        {(stuck || failed) && !isPractice && (
          <div className={`ts-sync ${failed ? 'err' : ''}`} role="status">
            <span>{failed || `${pending} rating${pending === 1 ? '' : 's'} not saved yet. Keep this tab open.`}</span>
            {!failed && <button type="button" onClick={retryReviews}>Retry now</button>}
          </div>
        )}

        {nextSubjects.length > 0 && (
          <div className="next-subjects">
            <p className="next-subjects-label">Continue with:</p>
            <div className="next-subjects-list">
              {nextSubjects.map(s => (
                <button key={s} className="next-subject-btn" onClick={() => navigate(`${backPath}/${encodeURIComponent(s)}`)}>
                  {s} →
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="done-actions">
          <Link to={backPath} className="btn btn-primary btn-lg">← All Subjects</Link>
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
        </div>
      </div>
    );
  }

  if (!current) return (
    <div className="study-loading">
      <div className="spinner" style={{ width: 44, height: 44 }} />
      <p>Resuming session...</p>
    </div>
  );

  const card       = current.card;
  const progress   = current.progress;
  const cardSubj   = card.subject || decodedSubject;
  const stability  = progress?.stability ?? 1;
  const interval   = progress?.interval ?? 0;
  const isLearning = progress?.isLearning !== false;
  const total      = queue.length;
  const canNext    = isReviewing ? findIdx(1) >= 0 : isPractice && done;
  const nextAction = () => (isReviewing ? step(1) : handlePracticeNext());

  // Interval labels come from the server's scheduler, so they always match what a rating does
  const previewInterval = button => current.preview?.[button] ?? '·';

  const cbTileCls = (i) => {
    const r = answers[i]?.res;
    return `cb-tile${i === viewIndex ? ' cur' : r ? ` r-${r}` : ''}${marked[i] ? ' mk' : ''}`;
  };

  const modeToggle = decodedSubject && examType === 'Prelims' ? (
    <div className="study-mode-toggle" role="group" aria-label="Study mode">
      <button className={isPractice ? 'active' : ''} onClick={() => changeStudyMode('practice')}>Practice</button>
      <button className={!isPractice ? 'active' : ''} onClick={() => changeStudyMode('remember')}>Remember</button>
    </div>
  ) : null;

  const syncBanner = (loadError || stuck || failed) ? (
    <div className={`ts-sync ${failed ? 'err' : ''}`} role="status">
      <span>{loadError || failed || `${pending} rating${pending === 1 ? '' : 's'} not saved yet. Retrying…`}</span>
      {!failed && !capReached && (
        <button type="button" onClick={loadError ? loadCards : retryReviews}>
          Retry now
        </button>
      )}
    </div>
  ) : null;

  const reviewBanner = isReviewing && isPractice ? (
    <div className="fc-review-banner">
      📖 Reviewing question {viewIndex + 1} · {isPractice ? 'answered' : 'rating disabled'} ·{' '}
      <button className="review-goto-current" onClick={() => goTo(queueIndex)}>Jump to current →</button>
    </div>
  ) : null;

  // Question, options, answer, comments: identical in both modes
  const questionBlock = (
    <>
      <div
        onClick={!mcq && !done ? handleReveal : undefined}
        style={!mcq && !done ? { cursor: 'pointer' } : undefined}
      >
        <QuestionCard
          card={card}
          picked={sel}
          done={done}
          onPick={handleOptionSelect}
          showTags
          actions={
            <>
              <ShareButton cardId={card._id} />
              <BookmarkButton
                key={card._id}
                cardId={card._id}
                isBookmarked={Boolean(progress?.isBookmarked)}
                onChange={isBookmarked => handleBookmarkChanged(card._id, isBookmarked)}
              />
            </>
          }
        />
      </div>

      {!mcq && !done && (
        <>
          {card.hints?.length > 0 && <div className="fc-hint">💡 {card.hints[0]}</div>}
          {isPractice && <div className="fc-tap-prompt">Tap the question or press Show Answer</div>}
        </>
      )}

      {done && <AnswerBlock card={card} />}
      {done && card.sourceTitle && <div className="fc-source-inline">📰 {card.sourceTitle}</div>}
      {done && (
        <div className="cb-comments">
          <CommentSection cardId={card._id} isLoggedIn={true} />
        </div>
      )}
    </>
  );

  // Practice and Remember share the CardBrowser question shell.
  return createPortal(
    <div className="cb cbx-portal cb-study">
      <aside className={`cb-side ${paletteOpen ? 'open' : ''}`} aria-label="Questions">
        <img className="cb-side-art" src={templeImg} alt="" />
        <div className="cb-side-body">
          <div className="cb-side-top">
            <Link to="/" className="cb-logo" aria-label="CivilFox home"><img src={logoImg} alt="" /></Link>
            <button type="button" className="cb-x" onClick={() => setPaletteOpen(false)} aria-label="Close"><X size={18} /></button>
          </div>

          <nav className="cb-subjects">
            {groups.map(([s, idxs]) => (
              <section key={s} className={idxs.includes(viewIndex) ? 'on' : ''}>
                <h2><i className="cb-dot" />{s}</h2>
                <div className="cb-tiles">
                  {idxs.map((i, n) => (
                    <button key={i} type="button" className={cbTileCls(i)} disabled={i > queueIndex}
                      aria-label={`${s} ${n + 1}`} onClick={() => { goTo(i); setPaletteOpen(false); }}>{n + 1}</button>
                  ))}
                </div>
              </section>
            ))}
          </nav>

          <button type="button" className="cb-back" onClick={() => navigate(backPath)}><ArrowLeft size={18} />Go back</button>
        </div>
      </aside>

      <div className="cb-content" ref={contentRef}>
        {!isPractice && syncBanner}
        <div className="cb-bar">
          <button type="button" className="cb-grid-btn" onClick={() => setPaletteOpen(true)} aria-label="All questions">
            <LayoutGrid size={18} />
          </button>
          <div className="cbx-bar">
            <span className="cbx-tally">
              <b className="c">{tally.c}</b> correct · <b className="w">{tally.w}</b> wrong · <b>{tally.left}</b> left
            </span>
            {isPractice && modeToggle}
          </div>
        </div>

        <article key={viewIndex}>
          {reviewBanner}
          {!isPractice && (
            <div className="cbx-review-state">
              <span>{cardSubj} · Question {viewIndex + 1} of {total}</span>
              <span>{isLearning ? 'Learning' : `Stability ${stability.toFixed(1)} · ${interval}d interval`}</span>
            </div>
          )}
          {questionBlock}

          <div className="cb-nav">
            <button type="button" className="cb-nbtn" onClick={() => step(-1)} disabled={findIdx(-1) < 0} aria-label="Previous"><ChevronLeft size={18} /></button>
            <span className="cb-pos">{viewIndex + 1}/{total}</span>
            <button type="button" className="cb-nbtn" onClick={nextAction} disabled={!canNext} aria-label="Next"><ChevronRight size={18} /></button>
          </div>

          {!isPractice && done && !isReviewing && (
            <div className="anki-buttons cb-study-rating slide-up">
              <div className="anki-btn-row">
                {[
                  { button: 'again', label: 'Again' },
                  { button: 'hard',  label: 'Hard'  },
                  { button: 'good',  label: 'Good'  },
                  { button: 'easy',  label: 'Easy'  },
                ].map(({ button, label }) => (
                  <button key={button} className={`anki-btn ${button}`} onClick={() => handleRate(button)} disabled={capReached}>
                    <span className="anki-btn-name">{label}</span>
                    <span className="anki-btn-interval">{previewInterval(button)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {(isPractice || (!done && !mcq)) && (
            <div className="cbx-actions">
              {!done && !mcq && <button type="button" className="cbx-btn primary" onClick={handleReveal}>Show answer</button>}
              {isPractice && (
                <>
                  {examType === 'Prelims' && (
                    <button type="button" className="cbx-btn" onClick={() => setMarked(m => ({ ...m, [viewIndex]: !m[viewIndex] }))}>
                      {marked[viewIndex] ? 'Unmark review' : 'Mark for review'}
                    </button>
                  )}
                  <button type="button" className="cbx-btn" onClick={finish}>Finish practice</button>
                </>
              )}
            </div>
          )}
        </article>
      </div>

      <div className={`cb-backdrop ${paletteOpen ? 'show' : ''}`} onClick={() => setPaletteOpen(false)} />
    </div>,
    document.body
  );
}